"use client";

import { GetVariantByIdDocument, GetLiveVariantDetailsDocument } from "../generated/graphql";
import { localeConstants } from "@/apiUtils/constants";
import { getClientCookie } from "@/utils/common";
import { AvailableGifts, LiveVariantDetails } from "@/common/lib/types";
import { variantDetailsToAvailableGift } from "@/utils/cartGiftUtils";
import PublicSaleorClient from "./PublicSaleorClient";

/**
 * Client-side function to get live variant details
 * @param id Variant ID
 * @param channel Channel ID
 * @returns Promise<LiveVariantDetails | null>
 */
export async function getLiveVariantDetailsClient(
  id: string,
  channel: string
): Promise<LiveVariantDetails | null> {
  try {
    const saleorClient = new PublicSaleorClient();
    const locale = getClientCookie(localeConstants.NEXT_LOCALE) || "";

    if (!id || !channel) {
      return null;
    }

    await saleorClient.init(locale);

    const response: { data: { productVariant: LiveVariantDetails }; errors: unknown } = await saleorClient.post(
      `${GetLiveVariantDetailsDocument}`,
      { id, channel },
      "no-store"
    );

    if (response?.errors) {
      return null;
    }

    return response?.data?.productVariant || null;
  } catch (error) {
    console.error("Error fetching live variant details:", error);
    return null;
  }
}

/** Client-side fetch of full gift variant catalog data for instant cart display. */
export async function getGiftVariantDetailsClient(
  id: string,
  channel: string,
): Promise<AvailableGifts | null> {
  try {
    const saleorClient = new PublicSaleorClient();
    const locale = getClientCookie(localeConstants.NEXT_LOCALE) || "";

    if (!id || !channel) {
      return null;
    }

    await saleorClient.init(locale);

    const response: {
      data: { productVariant: Parameters<typeof variantDetailsToAvailableGift>[0] | null };
      errors: unknown;
    } = await saleorClient.post(`${GetVariantByIdDocument}`, { id, channel, includePromotionPreview: false }, "no-store");

    if (response?.errors || !response?.data?.productVariant) {
      return null;
    }

    return variantDetailsToAvailableGift(response.data.productVariant);
  } catch (error) {
    console.error("Error fetching gift variant details:", error);
    return null;
  }
} 